import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useToast } from "../../hooks/use-toast";
import { useNavigate, useParams } from "react-router-dom";
import PTSNavigationRoutes from "../../utils/navigationRoutes";
import PTSLogo from "../../assets/us-logo.png";
import { AuthService } from "@/services/APIs";
import bgImage from "@/project/PTS/assets/pts-bg.jpg";
import {
  validatePassword,
  getPasswordRequirementsText,
  type PasswordValidationResult,
} from "../../utils/passwordValidation";

export default function ForgotPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] =
    useState<PasswordValidationResult | null>(null);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const { tokenId } = useParams<{ tokenId: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Form validation
    if (!newPassword || !confirmPassword) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    // Comprehensive password validation
    const validation = validatePassword(newPassword);
    setPasswordValidation(validation);

    if (!validation.isValid) {
      toast({
        title: "Password Validation Error",
        description:
          validation.errors[0] || "Password does not meet requirements",
        variant: "destructive",
      });
      return;
    }

    // Validate password match
    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match!",
        variant: "destructive",
      });
      return;
    }

    if (!tokenId) {
      toast({
        title: "Error",
        description: "Invalid reset link. Please request a new password reset.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await AuthService.forgotPasswordReset(tokenId, {
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      if (response) {
        toast({
          title: "Success",
          description:
            "Password reset successfully! You can now login with your new password.",
        });
        // Navigate to admin login page
        navigate(PTSNavigationRoutes.ADMIN_LOGIN_PAGE);
      }
    } catch (error: any) {
      console.error("Password reset failed:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Failed to reset password. Please try again or request a new reset link.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-gradient-to-br bg-cover bg-center bg-no-repeat p-4"
      style={{
        backgroundImage: `
        linear-gradient(to bottom right,
          rgba(0, 0, 0, 0.2),
          rgba(0, 0, 0, 0.2)
        ),
        url(${bgImage})
      `,
      }}
    >
      <Card className="w-full max-w-md bg-white/90">
        <CardHeader className="text-center">
          <img src={PTSLogo} alt="PTS Logo" className="mx-auto mb-4 w-24" />
          <CardTitle className="text-2xl">Reset Password</CardTitle>
          <CardDescription>Enter your new password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="new-password">New Password</Label>
                <button
                  type="button"
                  onClick={() =>
                    setShowPasswordRequirements(!showPasswordRequirements)
                  }
                  className="text-primary text-xs hover:underline"
                >
                  {showPasswordRequirements
                    ? "Hide Requirements"
                    : "Show Requirements"}
                </button>
              </div>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password (8-10 characters)"
                  value={newPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newPass = e.target.value;
                    setNewPassword(newPass);
                    if (newPass) {
                      const validation = validatePassword(newPass);
                      setPasswordValidation(validation);
                    } else {
                      setPasswordValidation(null);
                    }
                  }}
                  className={`pr-10 ${
                    passwordValidation && !passwordValidation.isValid
                      ? "border-red-500 focus-visible:ring-red-500"
                      : passwordValidation?.isValid
                        ? "border-green-500 focus-visible:ring-green-500"
                        : ""
                  }`}
                  required
                  minLength={8}
                  maxLength={10}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {!showNewPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {passwordValidation && passwordValidation.errors.length > 0 && (
                <div className="rounded-md bg-red-50 p-2 text-xs text-red-800">
                  <ul className="list-disc space-y-1 pl-4">
                    {passwordValidation.errors.slice(0, 3).map(error => (
                      <li key={error}>{error}</li>
                    ))}
                    {passwordValidation.errors.length > 3 && (
                      <li>
                        ...and {passwordValidation.errors.length - 3} more
                      </li>
                    )}
                  </ul>
                </div>
              )}
              {passwordValidation &&
                passwordValidation.isValid &&
                newPassword.length >= 8 && (
                  <div className="rounded-md bg-green-50 p-2 text-xs text-green-800">
                    ✓ Password meets all requirements
                  </div>
                )}
              {showPasswordRequirements && (
                <div className="rounded-md bg-blue-50 p-3 text-xs text-blue-900">
                  <p className="mb-2 font-semibold">Password Requirements:</p>
                  <ul className="list-disc space-y-1 pl-4">
                    {getPasswordRequirementsText().map(req => (
                      <li key={req}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const confirmPass = e.target.value;
                    setConfirmPassword(confirmPass);
                    // Real-time validation: show toast if passwords don't match
                    if (
                      confirmPass &&
                      newPassword &&
                      confirmPass !== newPassword
                    ) {
                      toast({
                        title: "Password Mismatch",
                        description:
                          "New password and confirm password do not match!",
                        variant: "destructive",
                      });
                    }
                  }}
                  onBlur={() => {
                    // Show toast on blur if passwords don't match
                    if (
                      confirmPassword &&
                      newPassword &&
                      confirmPassword !== newPassword
                    ) {
                      toast({
                        title: "Password Mismatch",
                        description:
                          "New password and confirm password do not match!",
                        variant: "destructive",
                      });
                    }
                  }}
                  className={`pr-10 ${
                    confirmPassword &&
                    newPassword &&
                    confirmPassword !== newPassword
                      ? "border-red-500 focus-visible:ring-red-500"
                      : confirmPassword &&
                          newPassword &&
                          confirmPassword === newPassword
                        ? "border-green-500 focus-visible:ring-green-500"
                        : ""
                  }`}
                  required
                  minLength={8}
                  maxLength={10}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {!showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {confirmPassword &&
                newPassword &&
                confirmPassword !== newPassword && (
                  <div className="rounded-md bg-red-50 p-2 text-xs text-red-800">
                    Passwords do not match!
                  </div>
                )}
              {confirmPassword &&
                newPassword &&
                confirmPassword === newPassword &&
                confirmPassword.length >= 8 && (
                  <div className="rounded-md bg-green-50 p-2 text-xs text-green-800">
                    ✓ Passwords match
                  </div>
                )}
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Resetting Password..." : "Reset Password"}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate(PTSNavigationRoutes.ADMIN_LOGIN_PAGE)}
                className="text-primary text-sm hover:underline"
              >
                Back to Sign In
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
